import React, { useEffect, useState } from "react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { notification } from "antd";
import Loader from "@appComponents/Loader/Loader";
import { MyTradesList } from "../components/MyTradesList/MyTradesList";
import { IMyTradeTransaction } from "../components/MyTradesList/IMyTradeTransaction";
import SelectV2 from "@appComponents/SelectV2";
import ETradeTypes from "@market/enums/TradeTypes";
import moment from "moment";
import CsvGenerator from "@appComponents/CsvGenerator/CsvGenerator";
import DownloadIcon from "../icons/download.svg";
import { useMediaQuery } from "react-responsive";

const typeOptions = [
  {
    value: "all",
    label: "All",
  },
  {
    value: ETradeTypes.buy,
    label: "Buy",
  },
  {
    value: ETradeTypes.sell,
    label: "Sell",
  },
];

const defaultDatePickerProps: Omit<ReactDatePickerProps, "onChange"> = {
  className: "tailwind-datepicker",
  withPortal: true,
  showYearDropdown: true,
  showMonthDropdown: true,
  dateFormat: "d MMM yyyy",
  isClearable: true,
};

export const MyTradesTab = () => {
  const [tradesLoading, setTradesLoading] = useState(true);
  const [trades, setTrades] = useState<IMyTradeTransaction[]>([]);
  const [typeFilter, setTypeFilter] = useState(typeOptions[0].value);
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [fromDateFilter, setFromDateFilter] = useState<Date | undefined>();
  const [toDateFilter, setToDateFilter] = useState<Date | undefined>();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    SecondaryMarketService.getMyTrades(1, 5)
      .then((resp) => {
        setTradesLoading(false);
        setTrades(Array.isArray(resp.data) ? resp.data : []);
        setFilteredTrades(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(() => {
        setTradesLoading(false);
        notification.error({ message: "Something went wrong." });
      });
    SecondaryMarketService.getSPVs();
  }, []);

  useEffect(() => {
    const filtered = trades.filter((trade) => {
      const tradeDate = moment(trade.created_at);

      const matchesType =
        typeFilter === "all" ? true : trade.type === typeFilter;

      const matchesFrom =
        !fromDateFilter ||
        tradeDate.isSameOrAfter(moment(fromDateFilter), "day");

      const matchesTo =
        !toDateFilter || tradeDate.isSameOrBefore(moment(toDateFilter), "day");

      return matchesType && matchesFrom && matchesTo;
    });

    setFilteredTrades(filtered);
  }, [typeFilter, fromDateFilter, toDateFilter, trades]);

  if (tradesLoading) {
    return <Loader />;
  }

  return (
    <div className="tailwind mt-[20px] md:mt-[30px] w-full">
      <div className="flex md:flex-row flex-col md:items-center justify-between">
        <div className="flex mb-[20px] md:mb-[50px] items-end w-full">
          <SelectV2
            label="Type"
            options={typeOptions}
            className="max-w-[180px] w-full"
            value={typeOptions.find((x) => x.value === typeFilter)}
            onChange={(opt) => setTypeFilter(opt.value)}
          />

          <div className="ml-[20px] max-w-[180px] md:max-w-full mr-2">
            <div className="text-[16px] normal-case font-semibold mb-4">
              Dates
            </div>
            <DatePicker
              {...defaultDatePickerProps}
              selected={fromDateFilter}
              onChange={setFromDateFilter}
              placeholderText="From"
            />
          </div>

          <div className="max-w-[180px] md:max-w-full md:mt-0 mt-2">
            <DatePicker
              {...defaultDatePickerProps}
              selected={toDateFilter}
              onChange={setToDateFilter}
              placeholderText="To"
            />
          </div>
        </div>

        <CsvGenerator
          baseEndpoint="/api/share_service/trades"
          endpointDetails={{}}
          noPagination={true}
          fileName="my-trades"
          hideText={isMobile}
          buttomClassname="border border-grey-lighter rounded-[8px] px-[15px] py-[7px] pr-[15px] bg-transparent h-[36px] text-sm"
          customIcon={<DownloadIcon className="md:ml-2 md:mr-0 mr-[15px]" />}
        />
      </div>

      <MyTradesList trades={filteredTrades} />
    </div>
  );
};
